import React, { useState, useCallback, useRef } from 'react';
import S3Service from '../utils/s3Service';
import './MenuItemImageUpload.css';

interface MenuItemImageUploadProps {
  menuItemId: string;
  images: {
    menuImage?: string;
    onlineImage?: string;
    qualityControlImages: string[];
  };
  onImagesUpdate: (images: {
    menuImage?: string;
    onlineImage?: string;
    qualityControlImages: string[];
  }) => void;
}

const MenuItemImageUpload: React.FC<MenuItemImageUploadProps> = ({
  menuItemId,
  images,
  onImagesUpdate
}) => {
  const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [success, setSuccess] = useState<{ [key: string]: boolean }>({});
  const [dragOver, setDragOver] = useState<{ [key: string]: boolean }>({});
  
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const setRef = useCallback((key: string) => (ref: HTMLInputElement | null) => {
    fileInputRefs.current[key] = ref;
  }, []);

  const updateState = useCallback((key: string, updates: any) => {
    setUploading(prev => ({ ...prev, [key]: updates.uploading ?? prev[key] }));
    setErrors(prev => ({ ...prev, [key]: updates.error ?? prev[key] }));
    setSuccess(prev => ({ ...prev, [key]: updates.success ?? prev[key] }));
    setDragOver(prev => ({ ...prev, [key]: updates.dragOver ?? prev[key] }));
  }, []);

  const handleFileUpload = useCallback(async (file: File, imageType: string) => {
    if (!file.type.startsWith('image/')) {
      updateState(imageType, { error: 'Please select a valid image file' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      updateState(imageType, { error: 'File size must be less than 10MB' });
      return;
    }

    updateState(imageType, { uploading: true, error: null, success: false });

    try {
      const result = await S3Service.uploadImage(file, imageType, menuItemId);
      
      if (result.success && result.url) {
        const updatedImages = { ...images };
        
        if (imageType === 'menu-image') {
          updatedImages.menuImage = result.url;
        } else if (imageType === 'online-image') {
          updatedImages.onlineImage = result.url;
        } else if (imageType.startsWith('quality-control')) {
          const index = parseInt(imageType.split('-')[2]) - 1;
          updatedImages.qualityControlImages[index] = result.url;
        }
        
        onImagesUpdate(updatedImages);
        updateState(imageType, { success: true });
        
        setTimeout(() => {
          updateState(imageType, { success: false });
        }, 3000);
      } else {
        updateState(imageType, { error: result.error || 'Upload failed' });
      }
    } catch (error) {
      updateState(imageType, { error: 'Upload failed. Please try again.' });
    } finally {
      updateState(imageType, { uploading: false });
    }
  }, [menuItemId, images, onImagesUpdate, updateState]);

  const handleFileSelect = useCallback((imageType: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileUpload(file, imageType);
      }
    };
  }, [handleFileUpload]);

  const handleDragOver = useCallback((imageType: string) => {
    return (e: React.DragEvent) => {
      e.preventDefault();
      updateState(imageType, { dragOver: true });
    };
  }, [updateState]);

  const handleDragLeave = useCallback((imageType: string) => {
    return (e: React.DragEvent) => {
      e.preventDefault();
      updateState(imageType, { dragOver: false });
    };
  }, [updateState]);

  const handleDrop = useCallback((imageType: string) => {
    return (e: React.DragEvent) => {
      e.preventDefault();
      updateState(imageType, { dragOver: false });
      
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileUpload(file, imageType);
      }
    };
  }, [handleFileUpload, updateState]);

  const handleRemoveImage = useCallback((imageType: string) => {
    const updatedImages = { ...images };
    
    if (imageType === 'menu-image') {
      updatedImages.menuImage = undefined;
    } else if (imageType === 'online-image') {
      updatedImages.onlineImage = undefined;
    } else if (imageType.startsWith('quality-control')) {
      const index = parseInt(imageType.split('-')[2]) - 1;
      updatedImages.qualityControlImages[index] = '';
    }
    
    onImagesUpdate(updatedImages);
    
    // Clear file input
    if (fileInputRefs.current[imageType]) {
      fileInputRefs.current[imageType]!.value = '';
    }
  }, [images, onImagesUpdate]);

  const handleUseForAll = useCallback((sourceUrl: string) => {
    const updatedImages = {
      menuImage: sourceUrl,
      onlineImage: sourceUrl,
      qualityControlImages: [sourceUrl, sourceUrl, sourceUrl, sourceUrl]
    };
    onImagesUpdate(updatedImages);
  }, [onImagesUpdate]);

  const renderImageUpload = (
    label: string,
    imageType: string,
    currentUrl?: string,
    showUseForAll = false
  ) => {
    const isUploading = uploading[imageType];
    const error = errors[imageType];
    const isSuccess = success[imageType];
    const isDragOver = dragOver[imageType];

    return (
      <div className="image-upload-container">
        <div className="image-upload-header">
          <label className="image-upload-label">{label}</label>
          {showUseForAll && currentUrl && (
            <button
              type="button"
              className="use-for-all-btn"
              onClick={() => handleUseForAll(currentUrl)}
              title="Use this image for all image types"
            >
              Use for All
            </button>
          )}
        </div>
        
        <div 
          className={`image-upload-area ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
          onDragOver={handleDragOver(imageType)}
          onDragLeave={handleDragLeave(imageType)}
          onDrop={handleDrop(imageType)}
          onClick={() => fileInputRefs.current[imageType]?.click()}
        >
          <input
            ref={setRef(imageType)}
            type="file"
            accept="image/*"
            onChange={handleFileSelect(imageType)}
            style={{ display: 'none' }}
          />
          
          {currentUrl ? (
            <div className="image-preview">
              <img src={currentUrl} alt={label} />
              <button
                type="button"
                className="remove-image"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveImage(imageType);
                }}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="upload-placeholder">
              <div className="upload-text">
                <p>Click to upload or drag and drop</p>
                <p className="upload-hint">PNG, JPG, GIF up to 10MB</p>
              </div>
            </div>
          )}
          
          {isUploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div className="progress-fill" />
              </div>
              <span>Uploading...</span>
            </div>
          )}
        </div>
        
        {error && (
          <div className="upload-error">
            <span>Error: {error}</span>
          </div>
        )}
        
        {isSuccess && (
          <div className="upload-success">
            <span>Upload successful!</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="menu-item-image-upload">
      <h3>Menu Item Images</h3>
      <p className="section-description">
        Upload images for different uses. You can upload one image and use it for all types by clicking "Use for All".
      </p>
      
      <div className="image-upload-grid">
        {/* Menu Image */}
        <div className="image-upload-column">
          {renderImageUpload('Menu Image', 'menu-image', images.menuImage, true)}
          <p className="image-description">
            Main image displayed on menus and POS systems
          </p>
        </div>
        
        {/* Online Image */}
        <div className="image-upload-column">
          {renderImageUpload('Online Image', 'online-image', images.onlineImage, true)}
          <p className="image-description">
            Image used for online ordering and delivery apps
          </p>
        </div>
        
        {/* Quality Control Images */}
        <div className="image-upload-column quality-control">
          <h4>Quality Control Images</h4>
          <p className="image-description">
            Reference images for kitchen staff to ensure consistent presentation
          </p>
          <div className="quality-control-grid">
            {[1, 2, 3, 4].map((index) => (
              <div key={index} className="quality-control-item">
                {renderImageUpload(
                  `QC Image ${index}`,
                  `quality-control-${index}`,
                  images.qualityControlImages[index - 1] || ''
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuItemImageUpload;